import { createSelector, createSlice, SliceCaseReducers } from '@reduxjs/toolkit';
import { Alternative } from 'app/components/Resources/ResourcesSteps/ResourcesStepsTwo/ResourcesStepsTwoAlternativesList/useResourcesAlternatives';

interface ResourcesStepsState {
  current_tabs: string[];
  filters: {
    companies: {
      uuid: string;
      name: string;
    }[];
    clients: {
      uuid: string;
      name: string;
    }[];
    industries: {
      uuid: string;
      name: string;
    }[];
    resource_types: {
      uuid: string;
      name: string;
    }[];
    formats: {
      uuid: string;
      name: string;
    }[];
  };
  alternatives: Alternative[];
  all_alternatives: Alternative[];
  showing_loading_screen: boolean;
  hide_tabs: boolean;
}

export const slice = createSlice<ResourcesStepsState, SliceCaseReducers<ResourcesStepsState>>({
  name: 'resources-steps',
  initialState: {
    current_tabs: ['most_relevant'],
    alternatives: [],
    all_alternatives: [],
    hide_tabs: false,
    filters: {
      companies: [],
      clients: [],
      industries: [],
      resource_types: [],
      formats: [],
    },
    showing_loading_screen: false,
  },
  reducers: {
    setCurrentTabs: (state, action) => {
      state.current_tabs = action.payload;
      state.filters = {
        companies: [],
        clients: [],
        industries: [],
        resource_types: [],
        formats: [],
      };
    },
    removeCurrentTab: (state, action) => {
      state.current_tabs = state.current_tabs.filter((tab) => tab !== action.payload);
    },
    pushCurrentTab: (state, action) => {
      state.current_tabs = [...state.current_tabs, action.payload];
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setFilter: (state, action) => {
      state.filters[action.payload.type] = action.payload.filters;
    },
    setAlternatives: (state, action) => {
      state.alternatives = action.payload;
    },
    setAllAlternatives: (state, action) => {
      state.all_alternatives = action.payload;
    },
    setShowingLoadingScreen: (state, action) => {
      state.showing_loading_screen = action.payload;
    },
    setHideTabs: (state, action) => {
      state.hide_tabs = action.payload;
    },
  },
});

export const {
  setCurrentTabs,
  setFilters,
  setFilter,
  setAlternatives,
  setAllAlternatives,
  setShowingLoadingScreen,
  removeCurrentTab,
  pushCurrentTab,
  setHideTabs,
} = slice.actions;

export const currentTabsSelector = (state): string[] => state.resourcesSteps.current_tabs;
export const hideTabsSelector = (state): boolean => state.resourcesSteps.hide_tabs;
export const allAlternativesSelector = (state): Alternative[] =>
  state.resourcesSteps.all_alternatives;

export const filtersSelector = (
  state
): {
  clients: {
    uuid: string;
    name: string;
  }[];
  companies: {
    uuid: string;
    name: string;
  }[];
  industries: {
    uuid: string;
    name: string;
  }[];
  resource_types: {
    uuid: string;
    name: string;
  }[];
  formats: {
    uuid: string;
    name: string;
  }[];
} => state.resourcesSteps.filters;

export const alternativesSelector = (state): Alternative[] => state.resourcesSteps.alternatives;

export const showingLoadingScreenSelector = (state): boolean =>
  state.resourcesSteps.showing_loading_screen;

export const companiesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const companiesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.uuidList.forEach((uuid) => {
        uuid.companies?.forEach((company) => {
          if (company?.uuid && !companiesMap.has(company.uuid)) {
            companiesMap.set(company.uuid, company);
          }
        });
      });
    });

    return Array.from(companiesMap.values());
  }
);

export const clientsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const clientsMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.uuidList.forEach((uuid) => {
        uuid.clients?.forEach((client) => {
          if (client?.uuid && !clientsMap.has(client.uuid)) {
            clientsMap.set(client.uuid, client);
          }
        });
      });
    });

    return Array.from(clientsMap.values());
  }
);

export const industriesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const industriesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.uuidList.forEach((uuid) => {
        uuid.industries?.forEach((industry) => {
          if (industry?.uuid && !industriesMap.has(industry.uuid)) {
            industriesMap.set(industry.uuid, industry);
          }
        });
      });
    });

    return Array.from(industriesMap.values());
  }
);

export const resourceTypesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const resourceTypesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.uuidList.forEach((uuid) => {
        if (uuid.type?.uuid && !resourceTypesMap.has(uuid.type.uuid)) {
          resourceTypesMap.set(uuid.type.uuid, uuid.type);
        }
      });
    });

    return Array.from(resourceTypesMap.values());
  }
);

export const resourceFormatsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector],
  (alternatives) => {
    const resourceFormatsMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.uuidList.forEach((uuid) => {
        if (uuid.format?.uuid && !resourceFormatsMap.has(uuid.format.uuid)) {
          resourceFormatsMap.set(uuid.format.uuid, uuid.format);
        }
      });
    });

    return Array.from(resourceFormatsMap.values());
  }
);

export const availableCompaniesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const {
      clients: selectedClients,
      formats: selectedResourceFormats,
      resource_types: selectedResourceTypes,
      industries: selectedIndustries,
    } = filters;

    const clientSet =
      selectedClients.length > 0 ? new Set(selectedClients.map((c) => c.uuid)) : null;
    const resourceFormatSet =
      selectedResourceFormats.length > 0
        ? new Set(selectedResourceFormats.map((f) => f.uuid))
        : null;
    const resourceTypeSet =
      selectedResourceTypes.length > 0 ? new Set(selectedResourceTypes.map((t) => t.uuid)) : null;
    const industrySet =
      selectedIndustries.length > 0 ? new Set(selectedIndustries.map((i) => i.uuid)) : null;

    const companiesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (!u?.uuid) return;

        if (clientSet && !u.clients?.some((client) => clientSet.has(client.uuid))) return;
        if (resourceFormatSet && !resourceFormatSet.has(u.format?.uuid)) return;
        if (resourceTypeSet && !resourceTypeSet.has(u.type?.uuid)) return;
        if (industrySet && !u.industries?.some((industry) => industrySet.has(industry.uuid)))
          return;

        u.companies?.forEach((company) => {
          if (company?.uuid && !companiesMap.has(company.uuid)) {
            companiesMap.set(company.uuid, company);
          }
        });
      });
    });

    return Array.from(companiesMap.values());
  }
);

export const availableClientsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const {
      companies: selectedCompanies,
      formats: selectedResourceFormats,
      resource_types: selectedResourceTypes,
      industries: selectedIndustries,
    } = filters;

    const companySet =
      selectedCompanies.length > 0 ? new Set(selectedCompanies.map((c) => c.uuid)) : null;
    const resourceFormatSet =
      selectedResourceFormats.length > 0
        ? new Set(selectedResourceFormats.map((f) => f.uuid))
        : null;
    const resourceTypeSet =
      selectedResourceTypes.length > 0 ? new Set(selectedResourceTypes.map((t) => t.uuid)) : null;
    const industrySet =
      selectedIndustries.length > 0 ? new Set(selectedIndustries.map((i) => i.uuid)) : null;

    const clientsMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (!u?.uuid) return;

        if (companySet && !u.companies?.some((company) => companySet.has(company.uuid))) return;

        if (resourceFormatSet && !resourceFormatSet.has(u.format?.uuid)) return;
        if (resourceTypeSet && !resourceTypeSet.has(u.type?.uuid)) return;
        if (industrySet && !u.industries?.some((industry) => industrySet.has(industry.uuid)))
          return;

        u.clients?.forEach((client) => {
          if (client?.uuid && !clientsMap.has(client.uuid)) {
            clientsMap.set(client.uuid, client);
          }
        });
      });
    });

    return Array.from(clientsMap.values());
  }
);

export const availableIndustriesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const {
      companies: selectedCompanies,
      formats: selectedResourceFormats,
      resource_types: selectedResourceTypes,
      clients: selectedClients,
    } = filters;

    const companySet =
      selectedCompanies.length > 0 ? new Set(selectedCompanies.map((c) => c.uuid)) : null;
    const resourceFormatSet =
      selectedResourceFormats.length > 0
        ? new Set(selectedResourceFormats.map((f) => f.uuid))
        : null;
    const resourceTypeSet =
      selectedResourceTypes.length > 0 ? new Set(selectedResourceTypes.map((t) => t.uuid)) : null;
    const clientSet =
      selectedClients.length > 0 ? new Set(selectedClients.map((c) => c.uuid)) : null;

    const industriesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (!u?.uuid) return;

        if (companySet && !u.companies?.some((company) => companySet.has(company.uuid))) return;
        if (resourceFormatSet && !resourceFormatSet.has(u.format?.uuid)) return;
        if (resourceTypeSet && !resourceTypeSet.has(u.type?.uuid)) return;
        if (clientSet && !u.clients?.some((client) => clientSet.has(client.uuid))) return;

        u.industries?.forEach((industry) => {
          if (industry?.uuid && !industriesMap.has(industry.uuid)) {
            industriesMap.set(industry.uuid, industry);
          }
        });
      });
    });

    return Array.from(industriesMap.values());
  }
);

export const availableResourceTypesFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const {
      companies: selectedCompanies,
      clients: selectedClients,
      formats: selectedResourceFormats,
      industries: selectedIndustries,
    } = filters;

    const companySet =
      selectedCompanies.length > 0 ? new Set(selectedCompanies.map((c) => c.uuid)) : null;
    const clientSet =
      selectedClients.length > 0 ? new Set(selectedClients.map((c) => c.uuid)) : null;
    const resourceFormatSet =
      selectedResourceFormats.length > 0
        ? new Set(selectedResourceFormats.map((f) => f.uuid))
        : null;
    const industrySet =
      selectedIndustries.length > 0 ? new Set(selectedIndustries.map((i) => i.uuid)) : null;

    const resourceTypesMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (!u?.uuid) return;

        if (companySet && !u.companies?.some((company) => companySet.has(company.uuid))) return;
        if (clientSet && !u.clients?.some((client) => clientSet.has(client.uuid))) return;
        if (resourceFormatSet && !resourceFormatSet.has(u.format?.uuid)) return;
        if (industrySet && !u.industries?.some((industry) => industrySet.has(industry.uuid)))
          return;

        if (u.type?.uuid && !resourceTypesMap.has(u.type.uuid)) {
          resourceTypesMap.set(u.type.uuid, u.type);
        }
      });
    });

    return Array.from(resourceTypesMap.values());
  }
);

export const availableResourceFormatsFromFilteredAlternativesSelector = createSelector(
  [alternativesSelector, filtersSelector],
  (alternatives, filters) => {
    const {
      companies: selectedCompanies,
      clients: selectedClients,
      industries: selectedIndustries,
      resource_types: selectedResourceTypes,
    } = filters;

    const companySet =
      selectedCompanies.length > 0 ? new Set(selectedCompanies.map((c) => c.uuid)) : null;
    const clientSet =
      selectedClients.length > 0 ? new Set(selectedClients.map((c) => c.uuid)) : null;
    const industrySet =
      selectedIndustries.length > 0 ? new Set(selectedIndustries.map((i) => i.uuid)) : null;
    const resourceTypeSet =
      selectedResourceTypes.length > 0 ? new Set(selectedResourceTypes.map((t) => t.uuid)) : null;

    const resourceFormatsMap = new Map();

    alternatives.forEach((alternative) => {
      alternative.originalUuidList.forEach((u) => {
        if (!u?.uuid) return;

        if (companySet && !u.companies?.some((company) => companySet.has(company.uuid))) return;
        if (clientSet && !u.clients?.some((client) => clientSet.has(client.uuid))) return;
        if (industrySet && !u.industries?.some((industry) => industrySet.has(industry.uuid)))
          return;
        if (resourceTypeSet && !resourceTypeSet.has(u.type?.uuid)) return;

        if (u.format?.uuid && !resourceFormatsMap.has(u.format.uuid)) {
          resourceFormatsMap.set(u.format.uuid, u.format);
        }
      });
    });

    return Array.from(resourceFormatsMap.values());
  }
);

export default slice.reducer;
